import {
  ArrowsAltOutlined,
  CheckCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import { Alert, Button, Flex } from 'antd';
import { ReactComponent as SuccessDossierIcon } from 'assets/icons/SuccessDossier.svg';
import { clsx } from 'clsx';
import { ChevronSteps } from 'components/ChevronSteps';
import { MultiStepModal } from 'components/Modal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import CloseConfirmationModal from 'views/dossiers/ajout/components/CloseConfirmationModal';
import MinimizedDraftButton from 'views/dossiers/ajout/components/MinimizedDraftButton';
import BeneficiaryForm from 'views/dossiers/ajout/forms/Beneficiary.form';
import ProjectInformationsForm from 'views/dossiers/ajout/forms/ProjectInformations.form';
import SiteInformationsForm from 'views/dossiers/ajout/forms/SiteInformations.form';
import useAjoutDossierFormController from 'views/dossiers/ajout/hooks/useAjoutDossierFormController';

interface AjoutDossierFormProps {
  openRequestId: number;
  onSuccess?: () => void;
}

interface StepIndicatorProps {
  steps: { title: string; key: number }[];
  currentStep: number;
  onMinimize: () => void;
  onClose: () => void;
  collapseLabel: string;
  closeLabel: string;
}

function StepIndicator({
  onClose,
  onMinimize,
  collapseLabel,
  closeLabel,
  ...props
}: StepIndicatorProps) {
  return (
    <div className="w-full flex items-center justify-between px-6 bg-white border-b-[1px] border-color-border">
      <div className="flex items-center bg-white">
        <ChevronSteps {...props} />
      </div>
      <div className="flex items-center gap-3">
        <Button
          type="link"
          size="small"
          icon={<ArrowsAltOutlined />}
          onClick={onMinimize}
          aria-label={collapseLabel}
          title={collapseLabel}
          className="!w-6 !h-6 !p-0 flex items-center justify-center !text-gris-hellio hover:!text-gray-600 transition-colors"
        />
        <Button
          type="link"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label={closeLabel}
          title={closeLabel}
          className="!w-6 !h-6 !p-0 flex items-center justify-center !text-gris-hellio hover:!text-gray-600 transition-colors"
        />
      </div>
    </div>
  );
}

export default function AjoutDossierForm({
  openRequestId,
  onSuccess,
}: AjoutDossierFormProps) {
  const { t } = useTranslation(['ajout_dossier', 'global']);

  const { state, ui, forms, actions } = useAjoutDossierFormController({
    openRequestId,
    onSuccess,
  });

  if (!state.isHydrated) {
    return null;
  }

  const stepItems = [
    {
      title: t('ajout_dossier:etapes:etape_1_short'),
      key: 0,
    },
    {
      title: t('ajout_dossier:etapes:etape_2_short'),
      key: 1,
    },
    {
      title: t('ajout_dossier:etapes:etape_3_short'),
      key: 2,
    },
  ];
  const requiredFieldsText = t('global:champs_requis');
  const [requiredTextBefore, requiredTextAfter] = requiredFieldsText.split('*');
  const errorMessage =
    state.error?.message || t('global:internet_connexion_error');

  /* Succès */
  return (
    <>
      <MultiStepModal
        open={state.isSuccessModalOpen}
        onCancel={actions.handleCloseSuccessModal}
      >
        <Flex
          vertical
          align="center"
          justify="center"
          className="text-center py-12 px-8 md:h-[85vh]"
        >
          <SuccessDossierIcon className="mb-6" />
          <div className="bg-emerald-50 rounded-lg px-6 py-4 mb-6 max-w-md">
            <p className="flex items-center justify-center gap-2 font-semibold text-gray-900 mb-1">
              <CheckCircleFilled className="text-green-600" />
              {t('ajout_dossier:success:title')}
            </p>
            <p className="text-sm text-gray-600 m-0">
              {t('ajout_dossier:success:text')}
            </p>
          </div>

          <Button
            type="primary"
            onClick={actions.handleCloseSuccessModal}
            shape="round"
            size="large"
          >
            {t('global:back_home')}
          </Button>
        </Flex>
      </MultiStepModal>

      <MultiStepModal
        open={state.isDraftActive && !state.isDraftMinimized}
        onCancel={actions.handleCloseRequest}
        destroyOnClose={false}
        wrapClassName="ajout-dossier-form-wrap"
        className={clsx(
          'ajout-dossier-form-modal',
          ui.isMinimizing && 'is-minimizing',
        )}
      >
        <div className="flex flex-col items-center md:h-[85vh]">
          <StepIndicator
            steps={stepItems}
            currentStep={state.currentSlide}
            onClose={actions.handleCloseRequest}
            onMinimize={actions.requestMinimize}
            collapseLabel={t('global:collapse')}
            closeLabel={t('global:close')}
          />

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-6 bg-white content">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {stepItems[state.currentSlide].title}
              </h2>
              <p className="text-sm text-gray-500">
                {requiredTextBefore}
                <span className="text-rouge-copro-6">*</span>
                {requiredTextAfter}
              </p>
            </div>

            <div
              key={ui.transitionKey}
              className={clsx(
                'animate-slide-in',
                ui.direction === 'next'
                  ? 'slide-from-right'
                  : 'slide-from-left',
              )}
            >
              <div className={clsx({ hidden: state.currentSlide !== 0 })}>
                <SiteInformationsForm
                  key={`site-form-${state.isDraftActive ? 'active' : 'inactive'}-${state.siteFormUiResetKey}`}
                  contactFormInstance={forms.siteContactFormInstance}
                  addressFormInstance={forms.siteAddressFormInstance}
                  onCompletionChange={actions.handleSiteCompletionChange}
                  initialIsComplete={state.isSiteStepComplete}
                  onValuesChange={actions.handleSiteValuesChange}
                />
              </div>

              <div className={clsx({ hidden: state.currentSlide !== 1 })}>
                <ProjectInformationsForm
                  formInstance={forms.projectFormInstance}
                  onValuesChange={actions.handleProjectValuesChange}
                />
              </div>

              <div className={clsx({ hidden: state.currentSlide !== 2 })}>
                <BeneficiaryForm
                  formInstance={forms.beneficiaryFormInstance}
                  siteContactInfo={ui.siteContactInfo}
                  onValuesChange={actions.handleBeneficiaryValuesChange}
                />
              </div>
            </div>
          </div>

          <div className="w-full border-t-[1px] border-color-border px-6 py-4 bg-white rounded-b-xl">
            {state.isError && (
              <Alert
                type="error"
                showIcon
                className="mb-4"
                message={errorMessage}
              />
            )}
            <Flex justify="space-between" align="center">
              <Button
                onClick={actions.handlePrevious}
                shape="round"
                className="border-bleu-hellio-500 text-bleu-hellio-500 hover:!text-bleu-hellio-600 hover:!border-bleu-hellio-600"
                size="large"
              >
                {state.currentSlide === 0
                  ? t('global:quitter')
                  : t('global:previous')}
              </Button>
              <Button
                loading={state.isLoading}
                shape="round"
                onClick={actions.handleNext}
                disabled={state.isLoading || !state.isFormSubmittable}
                type="primary"
                size="large"
              >
                {state.currentSlide === stepItems.length - 1
                  ? t('global:soumettre_mon_dossier')
                  : t('global:continuer')}
              </Button>
            </Flex>
          </div>
        </div>
      </MultiStepModal>

      {state.isDraftActive && state.isDraftMinimized && (
        <MinimizedDraftButton onClick={actions.expandDraft} />
      )}

      <div
        ref={ui.minimizeTargetRef}
        aria-hidden="true"
        className="fixed bottom-0 right-4 h-0 w-0"
      />

      <CloseConfirmationModal
        open={state.isCloseConfirmationOpen}
        onCancel={actions.handleCancelCloseRequest}
        onConfirm={actions.handleConfirmClose}
      />
    </>
  );
}
